import React from 'react';
import { Frame as FrameIcon, ZoomIn, ZoomOut, RotateCw, Camera, Upload, Mail } from 'lucide-react';
import { FRAME_PRESETS } from './framePresets';

/**
 * BottomControls
 *
 * The bottom-of-screen control bar for the ViewOnWall composer:
 *   - Frame picker (none / black / white / natural / gold)
 *   - Wall-width calibration slider
 *   - Scale +/-, reset, retake-photo, upload, save-&-email
 *
 * Extracted from `ViewOnWall.jsx` (which was approaching 1,000 lines).
 * Pure presentation — state stays in the parent.
 */
const BottomControls = ({
  framePreset, setFramePreset,
  wallWidthCm, setWallWidthCm,
  artworkScale, adjustScale, resetPosition,
  startCamera, fileInputRef, handleFileUpload,
  onOpenEmailModal,
}) => (
  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black/80 backdrop-blur-xl rounded-2xl p-4 flex flex-col items-center gap-3 border border-white/10 max-w-[95vw]">
    {/* Frame picker */}
    <div className="w-full flex items-center gap-3 px-2 pb-1 border-b border-white/10">
      <label className="text-white/80 text-xs whitespace-nowrap flex items-center gap-1.5">
        <FrameIcon className="w-3.5 h-3.5" />
        Frame
      </label>
      <div className="flex-1 flex items-center gap-1.5 flex-wrap">
        {Object.entries(FRAME_PRESETS).map(([key, p]) => {
          const active = framePreset === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFramePreset(key)}
              data-testid={`vow-frame-${key}`}
              className={`px-2.5 py-1 rounded-full text-[11px] transition-colors border ${active ? 'bg-white text-black border-white' : 'bg-white/5 text-white/80 border-white/15 hover:bg-white/10'}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
    </div>

    {/* Wall-width calibration slider */}
    <div className="w-full flex items-center gap-3 px-2 pb-1 border-b border-white/10">
      <label className="text-white/80 text-xs whitespace-nowrap" htmlFor="vow-wall-width">
        Wall width in photo
      </label>
      <input
        id="vow-wall-width"
        data-testid="vow-wall-width-slider"
        type="range"
        min="100"
        max="600"
        step="10"
        value={wallWidthCm}
        onChange={(e) => setWallWidthCm(parseInt(e.target.value, 10))}
        className="flex-1 accent-white"
      />
      <span className="text-white text-xs font-medium tabular-nums w-[64px] text-right">
        {wallWidthCm} cm
      </span>
    </div>

    {/* Scale + reset + photo controls */}
    <div className="flex items-center gap-3 sm:gap-4">
      <button onClick={() => adjustScale(-0.15)} className="p-3 hover:bg-white/10 rounded-xl transition-colors" title="Smaller" data-testid="vow-zoom-out-btn">
        <ZoomOut className="w-6 h-6 text-white" />
      </button>
      <div className="text-white text-base font-medium px-4 min-w-[100px] text-center">
        {Math.round(artworkScale * 100)}% Scale
      </div>
      <button onClick={() => adjustScale(0.15)} className="p-3 hover:bg-white/10 rounded-xl transition-colors" title="Larger" data-testid="vow-zoom-in-btn">
        <ZoomIn className="w-6 h-6 text-white" />
      </button>

      <div className="w-px h-8 bg-white/20" />

      <button onClick={resetPosition} className="p-3 hover:bg-white/10 rounded-xl transition-colors" title="Reset" data-testid="vow-reset-btn">
        <RotateCw className="w-6 h-6 text-white" />
      </button>

      <div className="w-px h-8 bg-white/20" />

      <button onClick={startCamera} className="p-3 hover:bg-white/10 rounded-xl transition-colors" title="Retake photo">
        <Camera className="w-6 h-6 text-white" />
      </button>
      <button onClick={() => fileInputRef.current?.click()} className="p-3 hover:bg-white/10 rounded-xl transition-colors" title="Upload different photo">
        <Upload className="w-6 h-6 text-white" />
      </button>

      <div className="w-px h-8 bg-white/20" />

      <button
        onClick={onOpenEmailModal}
        data-testid="vow-save-email-btn"
        title="Save & email this preview"
        className="px-4 py-2 bg-white text-black hover:bg-neutral-200 rounded-xl transition-colors text-sm font-medium flex items-center gap-2"
      >
        <Mail className="w-4 h-4" />
        <span className="hidden sm:inline">Save & email</span>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  </div>
);

export default BottomControls;
